import * as React from "react"
import { Row, Col, Spinner } from "react-bootstrap"
import { Subscription } from "rxjs"
import ToggleSwitch from "../FormattedInputs/ToggleSwitch"
import NotificationDetails from "../../api/Notifications/Model/NotificationDetails"
import NotificationService from "../../api/Notifications/Model/NotificationService"
import NotificationEvent from "../../api/Notifications/Model/NotificationEvent"
import NotificationDetailGet from "../../api/Notifications/NotificationDetailGet"

export interface NotificationSettings {
  showNotifications: boolean
  allServicesChecked: boolean
  allEventsChecked: boolean
  checkedNotificationServiceIDs: number[]
  checkedNotificationEventIDs: number[]
  notifyEmail: boolean
  notifyText: boolean
  notifyWeb: boolean
}

interface NotificationsSelectionProps extends NotificationSettings {
  canViewOrderTrackingGrid?: boolean
  onChange: (data: NotificationSettings) => void
}

interface NotificationsSelectionState {
  loading: boolean
  notificationServices?: NotificationService[]
  notificationEvents?: NotificationEvent[]
}

export default class NotificationSelection extends React.Component<NotificationsSelectionProps, NotificationsSelectionState> {
  state: NotificationsSelectionState = { loading: true }

  notificationDetailSubscription?: Subscription

  componentDidMount() {
    this.notificationDetailSubscription = NotificationDetailGet().subscribe(
      (data: NotificationDetails) => this.setState({ loading: false, notificationServices: data.NotificationService, notificationEvents: data.NotificationEvent }),
      () => this.setState({ loading: false })
    )
  }

  componentWillUnmount() {
    this.notificationDetailSubscription && this.notificationDetailSubscription.unsubscribe()
  }

  handleCheckChange = (what: keyof NotificationSettings, checked: boolean, id?: number) => {
    const data = this.props as NotificationSettings
    switch (what) {
      case "allServicesChecked":
        const { notificationServices } = this.state
        this.props.onChange({ ...data, allServicesChecked: checked, checkedNotificationServiceIDs: checked && notificationServices ? notificationServices.map((item: NotificationService) => item.NotificationServiceID) : [] })
        break
      case "allEventsChecked":
        const { notificationEvents } = this.state
        this.props.onChange({ ...data, allEventsChecked: checked, checkedNotificationEventIDs: checked && notificationEvents ? notificationEvents.filter((item: NotificationEvent) => !(!this.props.canViewOrderTrackingGrid && item.Title === "MRO_RES")).map((item: NotificationEvent) => item.NotificationEventID) : [] })
        break
      case "checkedNotificationServiceIDs":
        if (id) {
          const { allServicesChecked } = this.props
          const checkedNotificationServiceIDs = allServicesChecked
            ? this.state.notificationServices?.map(item => item.NotificationServiceID) || []
            : this.props.checkedNotificationServiceIDs
          const newChecked = checked ? [...checkedNotificationServiceIDs, id] : checkedNotificationServiceIDs.filter(itemID => itemID !== id)
          const allNewChecked = newChecked.length === this.state.notificationServices?.length
          this.props.onChange({ ...data, checkedNotificationServiceIDs: newChecked, allServicesChecked: allNewChecked })
        }
        break
      case "checkedNotificationEventIDs":
        if (id) {
          const { allEventsChecked } = this.props
          const checkedNotificationEventIDs = allEventsChecked
            ? this.state.notificationEvents?.filter((item: NotificationEvent) => !(!this.props.canViewOrderTrackingGrid && item.Title === "MRO_RES")).map(item => item.NotificationEventID) || []
            : this.props.checkedNotificationEventIDs
          const newChecked = checked ? [...checkedNotificationEventIDs, id] : checkedNotificationEventIDs.filter(itemID => itemID !== id)
          const allNewChecked = newChecked.length === this.state.notificationEvents?.length
          this.props.onChange({ ...data, checkedNotificationEventIDs: newChecked, allEventsChecked: allNewChecked })
        }
        break
      default:
        this.props.onChange({ ...data, [what]: checked })
        break
    }
  }

  render() {
    const { loading, notificationServices, notificationEvents } = this.state
    const { showNotifications, notifyEmail, notifyText, notifyWeb, checkedNotificationServiceIDs, checkedNotificationEventIDs, canViewOrderTrackingGrid } = this.props

    const allServicesChecked = this.props.allServicesChecked || (notificationServices?.length && notificationServices.length === checkedNotificationServiceIDs.length) === true
    const allEventsChecked = this.props.allEventsChecked || (notificationEvents?.length && notificationEvents.length === checkedNotificationEventIDs.length) === true
    return <>
      <Row>
        <Col className="pb-2">
          <p className="mb-0">
            Should we notify you about services and events?
            <ToggleSwitch className="ml-2" on="Yes" off="No" checked={showNotifications} onCheck={checked => this.handleCheckChange("showNotifications", checked)} />
          </p>
          <em className="small">Note: Your ability to receive a notification will depend on your access level. Contact your Administrator if you have questions about your access level.</em>
        </Col>
      </Row>
      <Row hidden={!showNotifications}>
        <Col>
          <div className="h4">Services&nbsp;{loading && <Spinner animation="border" variant="secondary" size="sm" className="mb-1" />}</div>
          <table className="notification-event-service">
            <tbody>
              <tr><td>All Services</td><td><ToggleSwitch on="Yes" off="No" checked={allServicesChecked} onCheck={checked => this.handleCheckChange("allServicesChecked", checked)} /></td></tr>
              {notificationServices?.map((service: NotificationService) => <tr key={service.NotificationServiceID}>
                <td>{service.Description}</td>
                <td><ToggleSwitch on="Yes" off="No" checked={allServicesChecked || checkedNotificationServiceIDs.includes(service.NotificationServiceID)} onCheck={checked => this.handleCheckChange("checkedNotificationServiceIDs", checked, service.NotificationServiceID)} /></td>
              </tr>)}
            </tbody>
          </table>
        </Col>
      </Row>
      <Row hidden={!showNotifications}>
        <Col>
          <div className="h4">Events&nbsp;{loading && <Spinner animation="border" variant="secondary" size="sm" className="mb-1" />}</div>
          <table className="notification-event-service">
            <tbody>
              <tr><td>All Events</td><td><ToggleSwitch on="Yes" off="No" checked={allEventsChecked} onCheck={checked => this.handleCheckChange("allEventsChecked", checked)} /></td></tr>
              {notificationEvents?.map((event: NotificationEvent) => <tr key={event.NotificationEventID}>
                <td>{event.Description}</td>
                <td><ToggleSwitch disabled={!canViewOrderTrackingGrid && event.Title === "MRO_RES"} on="Yes" off="No" checked={(canViewOrderTrackingGrid && allEventsChecked) || checkedNotificationEventIDs.includes(event.NotificationEventID)} onCheck={checked => this.handleCheckChange("checkedNotificationEventIDs", checked, event.NotificationEventID)} /></td>
              </tr>)}
            </tbody>
          </table>
        </Col>
      </Row>
      <Row hidden={!showNotifications}>
        <Col>
          <h4>How would you like to be notified?</h4>
          <table className="notification-event-service">
            <tbody>
              <tr>
                <td>Email</td>
                <td><ToggleSwitch on="Yes" off="No" checked={notifyEmail} onCheck={checked => this.handleCheckChange("notifyEmail", checked)} /></td>
              </tr>
              <tr>
                <td>Voice</td>
                <td><ToggleSwitch disabled on="Yes" off="No" onCheck={() => { }} /></td>
              </tr>
              <tr>
                <td>Text</td>
                <td><ToggleSwitch on="Yes" off="No" checked={notifyText} onCheck={checked => this.handleCheckChange("notifyText", checked)} /></td>
              </tr>
              <tr>
                <td>Web</td>
                <td><ToggleSwitch on="Yes" off="No" checked={notifyWeb} onCheck={checked => this.handleCheckChange("notifyWeb", checked)} /></td>
              </tr>
            </tbody>
          </table>
        </Col>
      </Row>
    </>
  }
}
